import {
  DailyTasksSortBy,
  DailyTasksStage,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasks";
import { DAILY_TASKS_HINTS } from "./DailyTasksHint";

export type DailyTasksLedgerKind =
  | "project"
  | "mockup"
  | "file"
  | "pr"
  | "po"
  | "raw"
  | "print"
  | "print_in";

export type DailyTasksHintKey = keyof typeof DAILY_TASKS_HINTS;

export type DailyTasksStageUi = {
  sortBy: DailyTasksSortBy;
  dateHint: DailyTasksHintKey;
  openWorkHint: DailyTasksHintKey;
  cycleHint: DailyTasksHintKey;
  slaShareHint: DailyTasksHintKey;
  /** DAY 0 axis label on the cycle spine (matches the cycle clock start). */
  cycleStartLabel: string;
  cycleTitle: string;
  cycleEndLabel: string;
  cycleAverageBlurb: string;
  activityHint: string;
  stageGridNote: string;
  ledger: DailyTasksLedgerKind;
  isSlaQueue: boolean;
};

const DEFAULT_UI: DailyTasksStageUi = {
  sortBy: "start_date",
  dateHint: "date",
  openWorkHint: "openWork",
  cycleHint: "cycle",
  slaShareHint: "slaShare",
  cycleStartLabel: "project approved",
  cycleTitle: "Project approved → mockups approved",
  cycleEndLabel: "mockups approved",
  cycleAverageBlurb:
    "Average days from project approved to mockup approval. The solid bar is time used; the hatch under it is the allowed SLA.",
  activityHint:
    "Counts when the logo, mockup, rework, reject, or approval happened — not when the project was approved.",
  stageGridNote:
    "Last mockup is for context and is not in the cycle SLA. Approval starts from the last upload.",
  ledger: "mockup",
  isSlaQueue: true,
};

export const DAILY_TASKS_STAGE_UI: Record<DailyTasksStage, DailyTasksStageUi> =
{
  projects: {
    ...DEFAULT_UI,
    sortBy: "project_priority",
    dateHint: "dateProject",
    openWorkHint: "openWorkProject",
    cycleHint: "cycleProject",
    slaShareHint: "slaShareProject",
    cycleTitle: "Created → finance + variants",
    cycleEndLabel: "finance + variants",
    cycleAverageBlurb:
      "Average days from project created until finance has approved and every product has variants covering ordered qty. The solid bar is time used; the hatch under it is the finance + variants SLA.",
    activityHint:
      "Counts when the project request, finance decision, variants covering, or customer invoice happened — not when the order was created.",
    stageGridNote:
      "Request → project and created → invoice are for context and are not in the cycle SLA.",
    ledger: "project",
  },
  mockup_upload: {
    ...DEFAULT_UI,
    sortBy: "mockup_priority",
    dateHint: "dateMockup",
  },
  mockup_approval: {
    ...DEFAULT_UI,
    sortBy: "mockup_priority",
    dateHint: "dateMockup",
    isSlaQueue: true,
  },
  file_upload: {
    ...DEFAULT_UI,
    sortBy: "file_priority",
    dateHint: "dateFile",
    openWorkHint: "openWorkFile",
    cycleHint: "cycleFile",
    slaShareHint: "slaShareFile",
    cycleTitle: "Mockup approval → sent",
    cycleEndLabel: "sent to printer",
    cycleAverageBlurb:
      "Average days from mockup approval to sent to printer. The solid bar is time used; the hatch under it is the allowed SLA.",
    activityHint:
      "Counts when the printing file was uploaded or sent to the printer — not when the order was created.",
    stageGridNote:
      "First/last file and first-file → sent are for context and are not in the cycle SLA.",
    ledger: "file",
  },
  cdr_to_printer: {
    ...DEFAULT_UI,
    sortBy: "file_priority",
    dateHint: "dateFile",
    openWorkHint: "openWorkFile",
    cycleHint: "cycleFile",
    slaShareHint: "slaShareFile",
    ledger: "file",
    isSlaQueue: true,
  },
  pr_creation: {
    ...DEFAULT_UI,
    sortBy: "pr_priority",
    dateHint: "datePr",
    openWorkHint: "openWorkPr",
    cycleHint: "cyclePr",
    slaShareHint: "slaSharePr",
    cycleTitle: "Variants covering → covering PR",
    cycleEndLabel: "covering PR",
    cycleAverageBlurb:
      "Average days from variants covering until clean PR quantity covers the ordered quantity. The solid bar is time used; the hatch under it is the allowed SLA.",
    activityHint:
      "Counts when the purchase request was created or covering PR qty was reached — not when the order was created.",
    stageGridNote:
      "First PR and approval steps are for context and are not in the cycle SLA.",
    ledger: "pr",
  },
  po_creation: {
    ...DEFAULT_UI,
    sortBy: "po_priority",
    dateHint: "datePo",
    openWorkHint: "openWorkPo",
    cycleHint: "cyclePo",
    slaShareHint: "slaSharePo",
    cycleTitle: "Project created → vendor",
    cycleEndLabel: "vendor email",
    cycleAverageBlurb:
      "Average days from project created until every issued purchase order is emailed to the vendor. The solid bar is time used; the hatch under it is the allowed SLA.",
    activityHint:
      "Counts when the purchase order was created, emailed to the vendor, or every approved PR got a PO — not when the order was created.",
    stageGridNote:
      "Finance → PO is for context and is not in the cycle SLA.",
    ledger: "po",
  },
  raw_inward: {
    ...DEFAULT_UI,
    sortBy: "raw_priority",
    dateHint: "dateRaw",
    openWorkHint: "openWorkRaw",
    cycleHint: "cycleRaw",
    slaShareHint: "slaShareRaw",
    cycleTitle: "PO created → QC",
    cycleEndLabel: "QC cleared",
    cycleAverageBlurb:
      "Average days from the first issued PO until every product is QC-cleared. The solid bar is time used; the hatch under it is the allowed SLA for that order type (inward + QC).",
    activityHint:
      "Counts when inward, full inward, QC close, reject, or hold happened — not when the order was created.",
    stageGridNote:
      "TAT cards use the same Ops clocks: PO created → full inward, first inward → QC cleared.",
    ledger: "raw",
  },
  raw_inward_qc: {
    ...DEFAULT_UI,
    sortBy: "raw_priority",
    dateHint: "dateRaw",
    openWorkHint: "openWorkRaw",
    cycleHint: "cycleRaw",
    slaShareHint: "slaShareRaw",
    ledger: "raw",
    isSlaQueue: true,
  },
  print_outward: {
    ...DEFAULT_UI,
    sortBy: "print_priority",
    dateHint: "datePrint",
    openWorkHint: "openWorkPrint",
    cycleHint: "cyclePrint",
    slaShareHint: "slaSharePrint",
    cycleTitle: "QC cleared → full outward",
    cycleEndLabel: "fully outward",
    cycleAverageBlurb:
      "Average days from the earliest QC clear until every QC-cleared custom product is fully outward. The solid bar is time used; the hatch under it is the print outward SLA.",
    activityHint:
      "Counts when print outwards, full outward, or qty outward happened — not when the order was created.",
    stageGridNote:
      "TAT cards use QC cleared → full outward. Complete when MAX(sent) covers ordered qty — stubs with 0 sent do not count.",
    ledger: "print",
  },
  print_inward: {
    ...DEFAULT_UI,
    sortBy: "print_in_priority",
    dateHint: "datePrintIn",
    openWorkHint: "openWorkPrintIn",
    cycleHint: "cyclePrintIn",
    slaShareHint: "slaSharePrintIn",
    cycleTitle: "Full outward → print QC cleared",
    cycleEndLabel: "print QC cleared",
    cycleAverageBlurb:
      "Average days from the earliest full outward until every fully outward custom product is print QC-cleared. The solid bar is time used; the hatch under it is the print inward + QC SLA.",
    activityHint:
      "Counts when print inward, full inward, QC close, or reject happened — not when the order was created.",
    stageGridNote:
      "TAT cards use full outward → full print inward, then first print inward → QC cleared. Complete by MAX received / net QC qty.",
    ledger: "print_in",
  },
  print_inward_qc: {
    ...DEFAULT_UI,
    sortBy: "print_in_priority",
    dateHint: "datePrintIn",
    openWorkHint: "openWorkPrintIn",
    cycleHint: "cyclePrintIn",
    slaShareHint: "slaSharePrintIn",
    ledger: "print_in",
    isSlaQueue: true,
  },
};

export function dailyTasksStageUi(stage: DailyTasksStage): DailyTasksStageUi {
  return DAILY_TASKS_STAGE_UI[stage] ?? DEFAULT_UI;
}

export function defaultSortByForStage(
  stage: DailyTasksStage,
): DailyTasksSortBy {
  return dailyTasksStageUi(stage).sortBy;
}
